import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  AtSign,
  Briefcase,
  CheckCircle,
  FileText,
  Info,
  Lock,
  ShieldAlert,
  XCircle,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { formatTimeAgo } from '@/lib/format'
import type { Notification, NotificationKind, NotificationLevel } from '../types'

interface Props {
  notification: Notification
  /** Called with the notification id when the user clicks it. The handler
   * decides whether to optimistically mark-read + close the dropdown. */
  onSelect?: (n: Notification) => void
  /** When true, the row renders compactly (used inside the bell preview). */
  dense?: boolean
  /** When true, render as `<button>` (used by the center page bulk-select rows
   * where the parent provides the link separately). Defaults to a `<Link>`. */
  asButton?: boolean
}

const ICON: Record<NotificationKind, React.ComponentType<{ size?: number; className?: string }>> = {
  alert_assigned: ShieldAlert,
  alert_status_changed: ShieldAlert,
  sla_warning: AlertTriangle,
  block_request_pending: Lock,
  block_approved: CheckCircle,
  block_rejected: XCircle,
  case_assigned: Briefcase,
  case_unassigned: Briefcase,
  sar_filed: FileText,
  mention: AtSign,
  system: Info,
}

const LEVEL_TONE: Record<NotificationLevel, string> = {
  critical: 'text-status-failed bg-status-failed/10',
  warning: 'text-status-pending bg-status-pending/10',
  info: 'text-muted-fg bg-muted',
}

export function NotificationItem({ notification: n, onSelect, dense, asButton }: Props) {
  const Icon = ICON[n.kind]

  const body = (
    <>
      <span
        className={cn(
          'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
          LEVEL_TONE[n.level],
        )}
      >
        <Icon size={12} aria-hidden />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-center gap-2">
          <span className={cn('text-sm', n.read_at === null && 'font-semibold')}>
            {n.title}
          </span>
          {n.read_at === null && (
            <span
              aria-label="Unread"
              className="h-1.5 w-1.5 rounded-full bg-primary"
            />
          )}
        </span>
        <span className="truncate text-xs text-muted-fg">{n.message}</span>
        <span className="text-[0.65rem] text-muted-fg">
          {formatTimeAgo(n.created_at)}
        </span>
      </span>
    </>
  )

  const cls = cn(
    'flex w-full items-start gap-2 rounded border border-transparent px-2 py-2 text-left transition-colors hover:border-border hover:bg-muted',
    dense ? 'py-1.5' : 'py-2',
    n.read_at === null ? 'bg-primary/[0.04]' : '',
  )

  if (asButton) {
    return (
      <button
        type="button"
        className={cls}
        onClick={() => onSelect?.(n)}
      >
        {body}
      </button>
    )
  }

  return (
    <Link to={n.link_url} onClick={() => onSelect?.(n)} className={cls}>
      {body}
    </Link>
  )
}
